<template>
  <div class="strip-question-editor p-1">
    <question-parts-editor
      :question="question"
      :api="api"
      @updated="$emit('updated')"
    />

    <!-- Cover replacement panel (for image questions) -->
    <div v-if="hasImageParts" class="mt-1">
      <b-button
        v-if="!coverPickerOpen"
        size="sm"
        variant="outline-primary"
        :disabled="!questionArtistId"
        @click="openCoverPicker"
      >
        <feather-icon icon="ImageIcon" /> Заменить обложки
      </b-button>
      <div v-else class="border rounded p-1 mt-1">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <strong>Выбор обложек</strong>
          <b-button size="sm" variant="link" @click="closeCoverPicker">Отмена</b-button>
        </div>
        <b-spinner v-if="loadingCovers" small />
        <div v-else class="covers-grid">
          <div
            v-for="c in coverOptions"
            :key="c.id"
            class="cover-option"
            :class="{ selected: selectedCoverIds.includes(c.id) }"
            @click="toggleCover(c.id)"
          >
            <img :src="c.url" alt="" />
            <div class="cover-label">{{ c.name || '—' }}</div>
          </div>
        </div>
        <b-button
          size="sm"
          variant="primary"
          :disabled="!selectedCoverIds.length || savingCovers"
          class="mt-1"
          @click="submitCovers"
        >
          <b-spinner v-if="savingCovers" small /> Применить ({{ selectedCoverIds.length }})
        </b-button>
      </div>
    </div>

    <!-- Stats -->
    <div class="stats-block mt-1 mb-1 p-1 border rounded">
      <h6 class="mb-1 d-flex align-items-center" style="gap: 6px">
        <feather-icon icon="TagIcon" /> Статистики
      </h6>
      <div class="d-flex flex-column" style="gap: 6px">
        <div v-for="kind in statKinds" :key="kind.key" class="d-flex align-items-center flex-wrap" style="gap: 4px">
          <small class="stat-label mr-1">{{ kind.label }}:</small>
          <b-badge
            v-for="item in (question[kind.key] || [])"
            :key="kind.key + '-' + item.id"
            :variant="kind.variant"
            class="d-inline-flex align-items-center"
            style="gap: 4px"
          >
            {{ item.name }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="cursor-pointer"
              @click="detachStat(kind.key, item.id)"
            />
          </b-badge>
          <v-select
            :options="availableLookup(kind.key)"
            label="name"
            :reduce="o => o.id"
            :clearable="false"
            placeholder="+ добавить"
            class="stat-add"
            @input="id => attachStat(kind.key, id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton, BBadge, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref, computed,
} from '@vue/composition-api'
import store from '@/store'
import QuestionPartsEditor from '@/views/question/QuestionPartsEditor.vue'

export default {
  components: {
    BButton, BBadge, BSpinner,
    vSelect, QuestionPartsEditor,
  },
  props: {
    stripId: { type: [String, Number], required: true },
    question: { type: Object, required: true },
    lookups: { type: Object, default: () => ({ genres: [], decades: [], origins: [] }) },
  },
  setup(props, { emit }) {
    const coverPickerOpen = ref(false)
    const loadingCovers = ref(false)
    const coverOptions = ref([])
    const selectedCoverIds = ref([])
    const savingCovers = ref(false)

    const hasImageParts = computed(() => (props.question.parts || []).some(p => p.type === 'image'))
    const questionArtistId = computed(() => {
      const artists = props.question.artists || []
      return artists.length ? artists[0].id : null
    })

    const statKinds = [
      { key: 'genres', label: 'Жанры', variant: 'light-info' },
      { key: 'decades', label: 'Декады', variant: 'light-warning' },
      { key: 'origins', label: 'Происхождение', variant: 'light-success' },
    ]

    const availableLookup = kind => {
      const all = props.lookups?.[kind] || []
      const taken = (props.question[kind] || []).map(i => i.id)
      return all.filter(item => !taken.includes(item.id))
    }

    const attachStat = (kind, id) => {
      if (!id) return
      const apiKind = kind === 'genres' ? 'genre' : kind === 'decades' ? 'decade' : 'origin'
      store.dispatch('strip-builder/attachStat', {
        stripId: props.stripId,
        questionId: props.question.id,
        kind: apiKind,
        id,
      }).then(() => emit('updated'))
    }

    const detachStat = (kind, id) => {
      const apiKind = kind === 'genres' ? 'genre' : kind === 'decades' ? 'decade' : 'origin'
      store.dispatch('strip-builder/detachStat', {
        stripId: props.stripId,
        questionId: props.question.id,
        kind: apiKind,
        id,
      }).then(() => emit('updated'))
    }

    const openCoverPicker = () => {
      if (!questionArtistId.value) return
      coverPickerOpen.value = true
      loadingCovers.value = true
      selectedCoverIds.value = []
      store.dispatch('strip-builder/fetchArtistCovers', { artistId: questionArtistId.value })
        .then(r => { coverOptions.value = r.data?.data || r.data || [] })
        .finally(() => { loadingCovers.value = false })
    }

    const closeCoverPicker = () => {
      coverPickerOpen.value = false
      coverOptions.value = []
      selectedCoverIds.value = []
    }

    const toggleCover = id => {
      const idx = selectedCoverIds.value.indexOf(id)
      if (idx >= 0) selectedCoverIds.value.splice(idx, 1)
      else selectedCoverIds.value.push(id)
    }

    const submitCovers = () => {
      savingCovers.value = true
      store.dispatch('strip-builder/replaceCovers', {
        stripId: props.stripId,
        questionId: props.question.id,
        coverIds: selectedCoverIds.value,
      })
        .then(() => {
          closeCoverPicker()
          emit('updated')
        })
        .finally(() => { savingCovers.value = false })
    }

    // Объект api для QuestionPartsEditor — оборачивает store для стрипов
    // (stripId/questionId прокидываются здесь, компонент о них не знает)
    const api = {
      updateQuestion: payload => store.dispatch('strip-builder/updateQuestion', { stripId: props.stripId, questionId: props.question.id, payload }),
      updatePart: (partId, payload) => store.dispatch('strip-builder/updatePart', { stripId: props.stripId, questionId: props.question.id, partId, payload }),
      deletePart: partId => store.dispatch('strip-builder/deletePart', { stripId: props.stripId, questionId: props.question.id, partId }),
      reorderParts: order => store.dispatch('strip-builder/reorderParts', { stripId: props.stripId, questionId: props.question.id, order }),
      addPart: payload => store.dispatch('strip-builder/addPart', { stripId: props.stripId, questionId: props.question.id, payload }),
      replaceAudioPart: (partId, payload) => store.dispatch('strip-builder/replaceAudioPart', { stripId: props.stripId, questionId: props.question.id, partId, payload }),
      searchArtists: q => store.dispatch('strip-builder/searchArtists', { q }).then(r => r.data?.data || r.data || []),
      fetchArtistTracks: artistId => store.dispatch('strip-builder/fetchArtistTracks', { artistId }).then(r => r.data?.data || r.data || []),
    }

    return {
      api,
      hasImageParts, questionArtistId,
      coverPickerOpen, loadingCovers, coverOptions, selectedCoverIds, savingCovers,
      statKinds, availableLookup, attachStat, detachStat,
      openCoverPicker, closeCoverPicker, toggleCover, submitCovers,
    }
  },
}
</script>

<style scoped>
.covers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}
.cover-option {
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  text-align: center;
  background: #fff;
}
.cover-option img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
}
.cover-option.selected {
  border-color: #7367f0;
}
.cover-label {
  font-size: 0.75rem;
  margin-top: 4px;
  color: #6e6b7b;
  word-break: break-word;
}
.stats-block {
  background: #fafbfd;
}
.stat-label {
  min-width: 100px;
  font-weight: 600;
  color: #5e5873;
}
.stat-add {
  min-width: 180px;
}
.stat-add :deep(.vs__dropdown-toggle) {
  min-height: 28px;
  padding: 0 4px;
}
</style>
